import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUser,
  FaArrowRight,
  FaPhone,
  FaEnvelope,
  FaShieldAlt,
  FaSyncAlt,
  FaTimesCircle,
  FaSpinner,
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Dashboard.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://uat.definiteassurance.com/api';
const COMPANY = 'Definite';

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatKES = (amount) => {
  const n = parseFloat(amount) || 0;
  return n.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
};

const formatDate = (input) => {
  try {
    if (!input) return '--';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(input));
  } catch {
    return '--';
  }
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
const Dashboard = () => {
  const navigate = useNavigate();

  // ── Auth ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsAuth] = useState(false);

  // ── Agent ──
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentCode] = useState('');
  const [agentName, setAgentName] = useState('');

  // ── Stats ──
  const [activePolicies, setActivePolicies] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [expired, setExpired] = useState([]);

  // ── Commission ──
  const [premium, setPremium] = useState(0);
  const [previous, setPrevious] = useState(0);
  const [remitted, setRemitted] = useState(0);

  // ── Chart ──
  const [chartData, setChartData] = useState([]);

  // ── Loading flags ──
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingChart, setLoadingChart] = useState(true);
  const [loadingCommission, setLoadingCommission] = useState(true);

  // ══════════════════════════════════════
  //  BOOT: credentials + agent in one effect
  // ══════════════════════════════════════
  useEffect(() => {
    // Read agent from storage (synchronous)
    const user = localStorage.getItem('definiteagent');
    if (!user) { navigate('/login'); return; }
    setAgent(user);

    // Fetch credentials (async) — sets isAuth flag when done
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsAuth(true);
      } catch {
        toast.error('Failed to initialise authentication.');
      }
    })();
  }, [navigate]);

  // ══════════════════════════════════════
  //  STEP 2: Agent details — gated on isAuth (same pattern as other pages)
  // ══════════════════════════════════════
  useEffect(() => {
    if (!agent || !isAuth) return;
    const headers = { 'Content-Type': 'application/json', Authorization: `Basic ${credentials}` };
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!data || !data.OID) throw new Error('Invalid agent response');
        setAgentCode(data.OID);
        setAgentName(data.Dealer_name || agent);
      } catch (err) {
        toast.error(`Failed to load agent details: ${err.message}`);
      }
    })();
  }, [agent, isAuth, credentials]);

  // ══════════════════════════════════════
  //  STEP 3: Dashboard data — gated on agentCode + isAuth
  // ══════════════════════════════════════
  useEffect(() => {
    if (!agentCode || !isAuth) return;
    const headers = { 'Content-Type': 'application/json', Authorization: `Basic ${credentials}` };

    // ── Policy stats ──
    const fetchStats = async () => {
      setLoadingStats(true);
      try {
        const [r1, r2, r3] = await Promise.all([
          fetch(`${API}/Activepolicies?agentCode=${agentCode}`, { headers }),
          fetch(`${API}/getUpcomingrenewals?agent=${agentCode}`, { headers }),
          fetch(`${API}/getExpired?agent=${agentCode}`, { headers }),
        ]);
        const [policies, upcomingRenewals, expiredData] = await Promise.all([
          r1.json(), r2.json(), r3.json(),
        ]);
        setActivePolicies(Array.isArray(policies) ? policies : []);
        setRenewals(Array.isArray(upcomingRenewals) ? upcomingRenewals : []);
        setExpired(Array.isArray(expiredData) ? expiredData : []);
      } catch {
        toast.error('Failed to load policy statistics.');
      } finally {
        setLoadingStats(false);
      }
    };

    // ── Commission + Remitted ──
    const fetchCommission = async () => {
      setLoadingCommission(true);
      try {
        const [r1, r2, r3] = await Promise.all([
          fetch(`${API}/getMonthearned?agent=${agentCode}`, { headers }),
          fetch(`${API}/getPreviousMonthearned?agent=${agentCode}`, { headers }),
          fetch(`${API}/getRemittedpremium?agent=${agentCode}`, { headers }),
        ]);
        const [thisMonth, lastMonth, remittedData] = await Promise.all([
          r1.json(), r2.json(), r3.json(),
        ]);
        setPremium(parseFloat(thisMonth.TotalCommission) || 0);
        setPrevious(parseFloat(lastMonth.TotalCommission) || 0);
        setRemitted(parseFloat(remittedData.RemittedPremium) || 0);
      } catch {
        toast.error('Failed to load commission data.');
      } finally {
        setLoadingCommission(false);
      }
    };

    // ── Monthly production chart ──
    const fetchChart = async () => {
      setLoadingChart(true);
      try {
        const res = await fetch(`${API}/monthly-summary?agent=${agentCode}`, { headers });
        const dt = await res.json();
        const data = Array.isArray(dt.data) ? dt.data : (Array.isArray(dt) ? dt : []);
        setChartData(
          data.map(item => ({
            month: item.month || '',
            NewBusiness: item.newBusiness || 0,
            Extension: item.extension || 0,
          }))
        );
      } catch {
        toast.error('Failed to load production chart data.');
      } finally {
        setLoadingChart(false);
      }
    };

    fetchStats();
    fetchCommission();
    fetchChart();
  }, [agentCode, isAuth, credentials]);

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="dashboard-layout">
      <ToastContainer position="top-right" autoClose={4000} />
      <Header userName={agentName || agent} />
      <Sidebar />

      <main className="dashboard-main">
        {/* ── Top row ── */}
        <div className="dash-top-row">
          <span className="dash-company-name">DEFINITE ASSURANCE</span>
          <span className="dash-breadcrumb">
            Dashboard / <span>Definite Assurance</span>
          </span>
        </div>

        {/* ══════════════════════════════════════
            HERO ROW: Welcome + 3 Stat Cards
           ══════════════════════════════════════ */}
        <div className="dash-hero-row">
          {/* Welcome card */}
          <div className="dash-welcome-card">
            <div>
              <h3>Welcome Back!</h3>
              <p>Definite Assurance Agents Portal</p>
            </div>
            <img src="/support.png" alt="Illustration" className="dash-welcome-illustration" />
            <div className="dash-welcome-bottom">
              <div className="dash-avatar"><FaUser /></div>
              <div>
                <div className="dash-welcome-stats">
                  <div className="dash-welcome-stat">
                    {loadingCommission
                      ? <FaSpinner className="dash-spinner-sm" />
                      : <span className="amount">KES {formatKES(remitted)}</span>
                    }
                    <span className="label">Remitted Premium</span>
                  </div>
                  <div className="dash-welcome-stat">
                    {loadingCommission
                      ? <FaSpinner className="dash-spinner-sm" />
                      : <span className="amount">KES {formatKES(premium)}</span>
                    }
                    <span className="label">Received Commission</span>
                  </div>
                </div>
                <div className="dash-agent-info">
                  <div className="dash-agent-name">{agentName || agent || '—'}</div>
                  <div className="dash-agent-role">Agent / Broker</div>
                  <div className="dash-agent-status">APPROVED</div>
                </div>
                <button className="dash-book-btn" onClick={() => navigate('/get-cover')}>
                  Book new cover <FaArrowRight />
                </button>
              </div>
            </div>
          </div>

          {/* Active Policies */}
          <div className="dash-stat-card" onClick={() => navigate('/active-policies')} style={{ cursor: 'pointer' }}>
            <div className="dash-stat-info">
              <h4>Active Policies</h4>
              {loadingStats
                ? <div className="value"><FaSpinner className="dash-spinner" /></div>
                : <div className="value">{activePolicies.length}</div>
              }
            </div>
            <div className="dash-stat-circle dark-green"><FaShieldAlt /></div>
          </div>

          {/* Upcoming Renewals */}
          <div className="dash-stat-card" onClick={() => navigate('/renewals/due')} style={{ cursor: 'pointer' }}>
            <div className="dash-stat-info">
              <h4>Upcoming Renewals</h4>
              {loadingStats
                ? <div className="value"><FaSpinner className="dash-spinner" /></div>
                : <div className="value">{renewals.length}</div>
              }
            </div>
            <div className="dash-stat-circle green"><FaSyncAlt /></div>
          </div>

          {/* Expired Policies */}
          <div className="dash-stat-card" onClick={() => navigate('/renewals/expired')} style={{ cursor: 'pointer' }}>
            <div className="dash-stat-info">
              <h4>Expired Policies</h4>
              {loadingStats
                ? <div className="value"><FaSpinner className="dash-spinner" /></div>
                : <div className="value">{expired.length}</div>
              }
            </div>
            <div className="dash-stat-circle red"><FaTimesCircle /></div>
          </div>
        </div>

        {/* ── Extension notice ── */}
        <div className="dash-notice">
          <p>
            TO EXTEND, NAVIGATE TO 'ACTIVE POLICIES', SEARCH FOR THE POLICY, THEN CLICK 'EXTEND'
          </p>
        </div>

        {/* ══════════════════════════════════════
            COMMISSION + PRODUCTION CHART
           ══════════════════════════════════════ */}
        <div className="dash-bottom-row">
          {/* Net Commission */}
          <div className="dash-card">
            <h4>Net Commission</h4>
            <div className="sub-label">This month</div>
            {loadingCommission ? (
              <div className="dash-loading-block"><FaSpinner className="dash-spinner" /></div>
            ) : (
              <>
                <div className="dash-commission-value">KES {formatKES(premium)}</div>
                <div className="dash-commission-last">
                  Last Month <span>KES {formatKES(previous)}</span>
                </div>
                <div className="dash-commission-remitted">
                  Remitted Premium <span>KES {formatKES(remitted)}</span>
                </div>
              </>
            )}
          </div>

          {/* Production Chart */}
          <div className="dash-chart-card">
            <h4>Monthly Production</h4>
            {loadingChart ? (
              <div className="dash-chart-loading"><FaSpinner className="dash-spinner" /> Loading chart…</div>
            ) : chartData.length === 0 ? (
              <div className="dash-chart-empty">No production data available.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value, name) => [`${value}`, name]}
                    contentStyle={{ fontSize: '0.8rem', borderRadius: 6 }}
                  />
                  <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
                  <Bar dataKey="NewBusiness" name="New Business" fill="#1a5c5a" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Extension" name="Extension" fill="#e74c3c" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ══════════════════════════════════════
            SUPPORT + UPCOMING RENEWALS TABLE
           ══════════════════════════════════════ */}
        <div className="dash-support-row">
          {/* Need support? */}
          <div className="dash-card dash-support-card">
            <h4>Need support?</h4>
            <p>Get in touch with us on:</p>
            <div className="dash-support-contacts">
              <a href="tel:+254708247247"><FaPhone /> +254 708 247 247</a>
              <a href="mailto:customerservice@definiteassurance.com"><FaEnvelope /> customerservice@definiteassurance.com</a>
            </div>
          </div>

          {/* Upcoming Renewals mini-table */}
          <div className="dash-card dash-renewals-card">
            <div className="dash-renewals-header">
              <h4>Upcoming Renewals</h4>
              {!loadingStats && renewals.length > 0 && (
                <button className="dash-renewals-viewall" onClick={() => navigate('/renewals/due')}>
                  View all →
                </button>
              )}
            </div>

            {loadingStats ? (
              <div className="dash-loading-block"><FaSpinner className="dash-spinner" /> Loading…</div>
            ) : renewals.length === 0 ? (
              <div className="dash-renewals-empty">No upcoming renewals found.</div>
            ) : (
              <table className="dash-renewals-table">
                <thead>
                  <tr>
                    <th>Policy No.</th>
                    <th>Client</th>
                    <th>Vehicle</th>
                    <th>Expiry Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {renewals.slice(0, 8).map((r, i) => (
                    <tr key={r.OID ?? i}>
                      <td>{r.PolicyNo ?? '--'}</td>
                      <td>{r.Dealer_name ?? '--'}</td>
                      <td>{r.Narration ?? '--'}</td>
                      <td>{formatDate(r.ExpiryDate)}</td>
                      <td><span className="dash-renewal-badge">DUE</span></td>
                    </tr>
                  ))}
                  {renewals.length > 8 && (
                    <tr>
                      <td colSpan={5} className="dash-renewals-more">
                        +{renewals.length - 8} more —{' '}
                        <button onClick={() => navigate('/renewals/due')}>View all</button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
